import React, { useState, useCallback, useEffect } from "react";
import { useStepNavigate } from "../hooks/useStepNavigate";
import { useOrder } from "../context/OrderContext";
import TablerLayout from "../components/layout/TablerLayout";
import StepperNavButton from "../components/stepper/StepperNavButton";
import Modal from "../components/modals/Modal";
import { IconAlertTriangle, IconCamera, IconPhoto, IconUpload, IconFile, IconX } from "@tabler/icons-react";
import { useDropzone } from 'react-dropzone';
import { dataService } from "../services/dataService";

const CsatolandoMedia = () => {
	const { order, updateOrder } = useOrder();
	const navigate = useStepNavigate();
	const [isMobile, setIsMobile] = useState(false);
	const [uploadLoading, setUploadLoading] = useState(false);
	const [currentUploadField, setCurrentUploadField] = useState(null);

	const [formData, setFormData] = useState({
		labfenykep: [],
		ambulans_lap: [],
		nyilatkozat: [],
		eukerd: [],
	});

	const [deleteModal, setDeleteModal] = useState({
		show: false,
		fieldName: null,
		index: null,
		loading: false,
	});

	const [errorModal, setErrorModal] = useState({
		show: false,
		title: "",
		message: "",
	});

	const [showImageOptions, setShowImageOptions] = useState(false);

	const fieldLabels = {
		labfenykep: "Lábfénykép",
		ambulans_lap: "Ambuláns lap",
		nyilatkozat: "Nyilatkozat",
		eukerd: "EÜ kérdőív",
	};

	const attachmentTypes = {
		labfenykep: 'labfenykep',
		ambulans_lap: 'ambulans_lap',
		nyilatkozat: 'telemedicina_nyilatkozat',
		eukerd: 'telemedicina_eu_kerdoiv',
	};

	const requiredFields = order.hmeliorder?.hmeliorder_order_type === 1 
		? ["labfenykep"] 
		: order.hmeliorder?.hmeliorder_order_type === 2 
		? ["labfenykep", "nyilatkozat", "eukerd"] 
		: order.hmeliorder?.hmeliorder_order_type === 3 
		? ["labfenykep"] 
		: [];

	const isFormValid = requiredFields.every((field) => formData[field] && formData[field].length > 0);

	useEffect(() => {
		const checkMobile = () => {
			const ua = navigator.userAgent;
			const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
			setIsMobile(isMobileDevice);
		};
		checkMobile();
		window.addEventListener('resize', checkMobile);
		return () => window.removeEventListener('resize', checkMobile);
	}, []);

	const convertToBase64 = (file) => {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.readAsDataURL(file);
			reader.onload = () => resolve(reader.result);
			reader.onerror = error => reject(error);
		});
	};

	const uploadImageToAPI = async (file, fieldName) => {
		try {
			const base64 = await convertToBase64(file);
			const base64Data = base64.split(',')[1];

			const imageData = {
				hmeliorder_szam: order.hmeliorder?.hmeliorder_szam,
				image: base64Data,
				file_name: file.name,
				content_type: file.type,
				attachment_type: attachmentTypes[fieldName],
			};

			const response = await dataService.uploadImage(imageData);
			
			if (response.data?.status === 'ok') {
				return response.data.data;
			} else {
				throw new Error(response.data?.message || 'Képfeltöltési hiba');
			}
		} catch (error) {
			console.error('Képfeltöltési hiba:', error);
			setErrorModal({
				show: true,
				title: "Feltöltési hiba",
				message: `Hiba történt a kép feltöltésekor, kérem, próbálja meg újra! <br /> ${error.message}`
			});
			return null;
		}
	};

	const processFile = async (file, fieldName) => {
		if (!file) return false;
		
		const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
		if (!validTypes.includes(file.type)) {
			setErrorModal({
				show: true,
				title: "Nem támogatott fájlformátum",
				message: "Csak JPG, PNG vagy PDF fájl tölthető fel!"
			});
			return false;
		}
		
		const maxSize = 5 * 1024 * 1024;
		if (file.size > maxSize) {
			setErrorModal({
				show: true,
				title: "Túl nagy fájlméret",
				message: "A fájl mérete nem lehet nagyobb 5MB-nál!"
			});
			return false;
		}
		
		try {
			const response = await uploadImageToAPI(file, fieldName);
			
			if (response) {
				const newFile = {
					type: file.type,
					name: file.name,
					size: file.size,
					originalFile: file,
					file_id: response.file_id || 1
				};

				setFormData(prev => ({
					...prev,
					[fieldName]: [...(prev[fieldName] || []), newFile]
				}));
				
				return true;
			}
			return false;
		} catch (error) {
			console.error('Fájl feldolgozási hiba:', error);
			return false;
		}
	};

	const handleFileChange = async (e, fieldName) => {
		const file = e.target.files[0];
		if (file) {
			setUploadLoading(true);
			await processFile(file, fieldName);
			setUploadLoading(false);
		}
		e.target.value = '';
	};

	const handleCameraCapture = async (e) => {
		const file = e.target.files[0];
		if (file && currentUploadField) {
			setUploadLoading(true);
			try {
				await processFile(file, currentUploadField);
			} catch (error) {
				console.error("Kamera feltöltés hiba:", error);
			} finally {
				setUploadLoading(false);
				setShowImageOptions(false);
				setCurrentUploadField(null);
			}
		}
		e.target.value = '';
	};

	const handleGalleryUpload = async (e) => {
		const file = e.target.files[0];
		if (file && currentUploadField) {
			setUploadLoading(true);
			try {
				await processFile(file, currentUploadField);
			} catch (error) {
				console.error("Galéria feltöltés hiba:", error);
			} finally {
				setUploadLoading(false);
				setShowImageOptions(false);
				setCurrentUploadField(null);
			}
		}
		e.target.value = '';
	};

	const onDrop = useCallback((acceptedFiles, fieldName) => {
		if (acceptedFiles.length > 0) {
			setUploadLoading(true);
			processFile(acceptedFiles[0], fieldName).finally(() => {
				setUploadLoading(false);
			});
		}
	}, []);

	const openMobileUpload = (fieldName) => {
		setCurrentUploadField(fieldName);
		setShowImageOptions(true);
	};

	const requestDelete = (fieldName, index) => {
		setDeleteModal({
			show: true,
			fieldName,
			index,
			loading: false,
		});
	};

	const confirmDelete = async () => {
		try {
			setDeleteModal(prev => ({...prev, loading: true}));
			
			const { fieldName, index } = deleteModal;
			const fileToDelete = (formData[fieldName] || [])[index];

			if (fileToDelete.file_id && fileToDelete.file_id !== 1) {
				const deleteData = {
					hmeliorder_szam: order.hmeliorder?.hmeliorder_szam,
					file_id: fileToDelete.file_id,
					attachment_type: attachmentTypes[fieldName]
				};
				
				const response = await dataService.deleteImage(deleteData);
				
				if (response.data?.status !== 'ok') {
					throw new Error(response.data?.message || 'Hiba történt a dokumentum törlésekor');
				}
			}

			setFormData((prev) => {
				const fieldArray = prev[fieldName] || [];
				if (fieldArray[index]?.originalFile) {
					try {
						URL.revokeObjectURL(URL.createObjectURL(fieldArray[index].originalFile));
					} catch (e) {
						console.log('Blob felszabadítási hiba:', e);
					}
				}
				return {
					...prev,
					[fieldName]: fieldArray.filter((_, i) => i !== index),
				};
			});

			setDeleteModal({
				show: false,
				fieldName: null,
				index: null,
				loading: false,
			});
			
		} catch (error) {
			console.error('Dokumentum törlési hiba:', error);
			setErrorModal({
				show: true,
				title: "Törlési hiba",
				message: `Hiba történt a dokumentum törlésekor, kérem, próbálja meg újra! <br /> ${error.message}`
			});
			
			setDeleteModal(prev => ({...prev, loading: false}));
		}
	};

	const cancelDelete = () => {
		setDeleteModal({
			show: false,
			fieldName: null,
			index: null,
			loading: false,
		});
	};

	const handleSubmit = () => {
		if (!isFormValid) {
			setErrorModal({
				show: true,
				title: "Hiányzó fájlok",
				message: "Minden kötelező dokumentumot fel kell tölteni!",
			});
			return;
		}

		updateOrder({
			...order,
			csatolmanyok: formData,
		});

		navigate("/Meretvetel");
	};

	const closeErrorModal = () => {
		setErrorModal({ show: false, title: "", message: "" });
	};

	// Dropzone instance-ok minden mezőhöz
	const labfenykepDropzone = useDropzone({ 
		onDrop: (files) => onDrop(files, 'labfenykep'),
		accept: {
			'image/jpeg': ['.jpg', '.jpeg'],
			'image/png': ['.png'],
			'application/pdf': ['.pdf']
		},
		maxFiles: 1
	});

	const ambulansLapDropzone = useDropzone({ 
		onDrop: (files) => onDrop(files, 'ambulans_lap'),
		accept: {
			'image/jpeg': ['.jpg', '.jpeg'],
			'image/png': ['.png'],
			'application/pdf': ['.pdf']
		},
		maxFiles: 1
	});

	const nyilatkozatDropzone = useDropzone({ 
		onDrop: (files) => onDrop(files, 'nyilatkozat'),
		accept: {
			'image/jpeg': ['.jpg', '.jpeg'],
			'image/png': ['.png'],
			'application/pdf': ['.pdf']
		},
		maxFiles: 1
	});

	const eukerdDropzone = useDropzone({ 
		onDrop: (files) => onDrop(files, 'eukerd'),
		accept: {
			'image/jpeg': ['.jpg', '.jpeg'],
			'image/png': ['.png'],
			'application/pdf': ['.pdf']
		},
		maxFiles: 1
	});

	const dropzoneMap = {
		labfenykep: labfenykepDropzone,
		ambulans_lap: ambulansLapDropzone,
		nyilatkozat: nyilatkozatDropzone,
		eukerd: eukerdDropzone,
	};

	useEffect(() => {
		// Meglévő csatolmányok betöltése
		if (order.csatolmanyok) {
			setFormData({
				labfenykep: order.csatolmanyok.labfenykep || [],
				ambulans_lap: order.csatolmanyok.ambulans_lap || [],
				nyilatkozat: order.csatolmanyok.nyilatkozat || [],
				eukerd: order.csatolmanyok.eukerd || [],
			});
		}
	}, [order.csatolmanyok]);

	const renderFileField = (fieldName, isRequired = false) => {
		const { getRootProps, getInputProps, isDragActive } = dropzoneMap[fieldName];
		const files = formData[fieldName] || [];

		return (
			<div className="mb-4" key={fieldName}>
				<label className="form-label">
					{fieldLabels[fieldName]}{isRequired && <span className="text-danger"> *</span>}
				</label>

				{/* Feltöltött fájlok listája */}
				{files.map((file, index) => (
					<div className="card mb-2 p-3 position-relative" key={index}>
						<div className="position-absolute top-0 end-0 m-2">
							<button 
								className="btn btn-sm btn-outline-danger rounded-circle" 
								onClick={() => requestDelete(fieldName, index)}
								type="button"
								disabled={uploadLoading || deleteModal.loading}
							>
								<IconX size={18} />
							</button>
						</div>

						{file.type && file.type.includes('image') ? (
						file.originalFile ? (
							<div className="text-center">
								<img 
									src={URL.createObjectURL(file.originalFile)} 
									alt="Előnézet" 
									className="img-thumbnail mb-2"
									style={{ maxWidth: '150px', maxHeight: '150px' }}
								/>
							</div>
						) : (
							<div className="d-flex align-items-center justify-content-center mb-2 py-3">
								<IconFile size={32} className="me-2 text-primary" />
								<span className="fs-6">Kép (nincs előnézet)</span>
								</div>
							)
						) : (
							<div className="d-flex align-items-center justify-content-center mb-2 py-3">
								<IconFile size={32} className="me-2 text-primary" />
								<span className="fs-6">PDF dokumentum</span>
							</div>
						)}

						<div className="small text-muted">
							{file.name || "Feltöltött fájl"} 
							{file.size && ` (${Math.round(file.size / 1024)} KB)`}
						</div>
					</div>
				))}

				{/* Desktop - Dropzone */}
				{!isMobile && (
					<>
						<div 
							{...getRootProps()} 
							className={`dropzone border border-2 rounded-3 p-4 text-center my-2 ${isDragActive ? 'border-primary bg-light' : ''}`}
							style={{ 
								borderStyle: isDragActive ? 'solid' : 'dashed',
								cursor: 'pointer',
								minHeight: '120px',
								display: 'flex',
								flexDirection: 'column',
								alignItems: 'center',
								justifyContent: 'center',
								backgroundColor: isDragActive ? '#f8f9fa' : 'transparent',
							}}
						>
							<input {...getInputProps()} />
							<IconUpload size={32} className="mb-2 text-primary" />
							<p className="mb-1 fw-bold">Húzza ide a fájlt vagy kattintson</p>
							<p className="text-muted mb-0 small">JPG, PNG, PDF (max. 5MB)</p>
						</div>
					</>
				)}

				{/* Mobil - Feltöltés gomb */}
				{isMobile && (
					<div className="d-grid gap-2 mt-2">
						<button
							type="button"
							className="btn btn-outline-primary"
							onClick={() => openMobileUpload(fieldName)}
							disabled={uploadLoading}
						>
							<IconPhoto className="me-2" size={20} />
							Dokumentum hozzáadása
						</button>
					</div>
				)}
			</div>
		);
	};

	return (
		<TablerLayout title="Szükséges dokumentumok csatolása">
			<div className="row justify-content-center" style={{ filter: errorModal.show || deleteModal.show || showImageOptions ? "blur(3px)" : "none" }}>
				<div className="col-md-10">
					{process.env.NODE_ENV === 'development' && (
						<div className="d-flex gap-2 flex-wrap mb-3">
							<button
								type="button"
								className="btn btn-warning btn-sm"
								onClick={() => setIsMobile(!isMobile)}
							>
								🧪 Eszköz: {isMobile ? 'Mobil' : 'Desktop'}
							</button>
						</div>
					)}

					{requiredFields.length > 0 || order.hmeliorder?.hmeliorder_order_type === 1 ? (
						<>
							{/* Kötelező mezők */}
							{requiredFields.map((fieldName) => renderFileField(fieldName, true))}

							{/* Opcionális ambuláns lap vényes és privát rendeléseknél */}
							{(order.hmeliorder?.hmeliorder_order_type === 1 || order.hmeliorder?.hmeliorder_order_type === 3) && 
								renderFileField("ambulans_lap", false)
							}
						</>
					) : (
						<p className="text-center fs-5">Nincs szükséges dokumentum feltöltés.</p>
					)}

					<div className="d-flex justify-content-between mt-4">
						<StepperNavButton side="left" label="Vissza" onClick={() => navigate("/BetetTipus")} />
						<StepperNavButton 
							side="right" 
							label="Tovább" 
							onClick={handleSubmit} 
							disabled={!isFormValid || uploadLoading} 
						/>
					</div>
				</div>
			</div>

			{/* Mobil - Kép opciók modal */}
			<Modal 
				isOpen={showImageOptions} 
				onClose={() => !uploadLoading && setShowImageOptions(false)}  
				title="Dokumentum hozzáadása"
				type="info"
				showCancel={!uploadLoading}
				cancelText="Mégsem"
				showFooter={!uploadLoading}
			>
				<div className="d-grid gap-3">
					<button
						className="btn btn-primary py-3"
						onClick={() => document.getElementById('camera-input').click()}
						disabled={uploadLoading}
					>
						{uploadLoading ? (
							<>
								<span className="spinner-border spinner-border-sm me-2" role="status"></span>
								Folyamatban...
							</>
						) : (
							<>
								<IconCamera className="me-2" size={24} />
								Fotó készítése
							</>
						)}
					</button>
					<button
						className="btn btn-outline-primary py-3"
						onClick={() => document.getElementById('gallery-input').click()}
						disabled={uploadLoading}
					>
						{uploadLoading ? (
							<>
								<span className="spinner-border spinner-border-sm me-2" role="status"></span>
								Folyamatban...
							</>
						) : (
							<>
								<IconPhoto className="me-2" size={24} />
								Dokumentum kiválasztása
							</>
						)}
					</button>
				</div>
				{uploadLoading && (
					<div className="alert alert-info mt-3">
						<div className="d-flex align-items-center mb-2">
							<div className="spinner-border text-primary me-3" role="status">
								<span className="visually-hidden">Feltöltés...</span>
							</div>
							<div className="fw-bold">Dokumentum feltöltése folyamatban...</div>
						</div>
						<div className="small mt-1">Kérjük, várjon, amíg a feltöltés befejeződik!</div>
					</div>
				)}
				<div className="mt-3 text-center text-muted small">
					Elfogadott formátumok: JPG, PNG, PDF (max. 5MB)
				</div>
			</Modal>

			{/* Rejtett mobil inputok */}
			<input 
				type="file" 
				className="d-none" 
				accept="image/*,.pdf" 
				onChange={handleGalleryUpload}
				id="gallery-input"
			/>
			<input 
				type="file" 
				className="d-none" 
				accept="image/*" 
				capture="environment"
				onChange={handleCameraCapture}
				id="camera-input"
			/>

			{/* Hiba modal */}
			<Modal 
				isOpen={errorModal.show} 
				onClose={closeErrorModal} 
				onConfirm={closeErrorModal}
				title={errorModal.title} 
				type="danger" 
				confirmText="Rendben" 
				confirmClass="btn btn-primary" 
				showCancel={false}
				icon={<IconAlertTriangle size={70} className="text-danger" />}
			>
				<div className="fs-5" dangerouslySetInnerHTML={{ __html: errorModal.message }}></div>
			</Modal>

			{/* Törlés megerősítő modal */}
			<Modal 
				isOpen={deleteModal.show} 
				onClose={cancelDelete} 
				onConfirm={confirmDelete} 
				title="Fájl törlése" 
				type="warning" 
				confirmText="Törlés" 
				secondaryText="Mégsem" 
				confirmClass="btn btn-danger" 
				secondaryClass="btn btn-outline-secondary" 
				showSecondary={true} 
				showCancel={false} 
				icon={<IconAlertTriangle size={70} className="text-warning" />}
				confirmDisabled={deleteModal.loading}
			>
				<h4 className="mb-3">Biztosan törölni szeretné ezt a fájlt?</h4>
				<div className="fs-5">
					A törölt fájl nem állítható vissza.
					{deleteModal.loading && (
						<div className="mt-2">
							<div className="spinner-border spinner-border-sm text-primary me-2" role="status">
								<span className="visually-hidden">Törlés...</span>
							</div>
							Törlés folyamatban...
						</div>
					)}
				</div>
			</Modal>
		</TablerLayout>
	);
};

export default CsatolandoMedia;
